import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Truck, MapPin, Phone, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'

// Greater Amman default — covers the whole service area at zoom 11.
const AMMAN_CENTER: [number, number] = [31.9515, 35.9239]

interface Delivery {
  order_id: string
  order_number: string
  status: string
  delivery_name: string | null
  delivery_phone: string | null
  delivery_address: string | null
  delivery_lat: number | null
  delivery_lng: number | null
  route_origin_lat: number | null
  route_origin_lng: number | null
  total_fils: number
  payment_method: string
  tip_fils: number
  out_for_delivery_at: string | null
  driver_id: string
  driver_name: string
  driver_phone: string
  driver_lat: number | null
  driver_lng: number | null
  driver_location_at: string | null
}

// Custom Leaflet icons rendered as inline SVG so we don't need to host PNG assets.
const driverIcon = L.divIcon({
  className: '',
  html: `<div style="
    width: 36px; height: 36px;
    background: #C41E3A;
    border: 3px solid white;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
  "><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11"/><path d="M14 9h4l4 4v4c0 .6-.4 1-1 1h-2"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
})

const destIcon = L.divIcon({
  className: '',
  html: `<div style="
    width: 30px; height: 30px;
    background: #1A1A1A;
    border: 2px solid #C41E3A;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
  "><div style="transform: rotate(45deg); color: #C41E3A; font-weight: 700; font-size: 14px;">📍</div></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 28],
})

export default function LiveMap() {
  const [autoCenter, setAutoCenter] = useState(true)

  const { data, isFetching, refetch, dataUpdatedAt } = useQuery<{ deliveries: Delivery[] }>({
    queryKey: ['admin-dispatch-live'],
    queryFn: () => api.get('/orders/dispatch/live').then(r => r.data),
    refetchInterval: 5_000, // near-real-time via polling
  })

  const deliveries = data?.deliveries ?? []
  const trackedCount = deliveries.length

  // Bounds = every coord we have (drivers + destinations). Used to fit the map
  // initially. Only run when autoCenter is enabled and we actually have points.
  const bounds = useMemo(() => {
    const points: [number, number][] = []
    for (const d of deliveries) {
      if (d.driver_lat != null && d.driver_lng != null) points.push([d.driver_lat, d.driver_lng])
      if (d.delivery_lat != null && d.delivery_lng != null) points.push([d.delivery_lat, d.delivery_lng])
    }
    return points.length > 0 ? L.latLngBounds(points) : null
  }, [deliveries])

  return (
    <div className="p-6 h-screen flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold text-white">Live Map</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            All active deliveries — ambassador positions update every 5 seconds.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 tabular-nums">
            {trackedCount} active {trackedCount === 1 ? 'delivery' : 'deliveries'}
          </span>
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input
              type="checkbox"
              checked={autoCenter}
              onChange={e => setAutoCenter(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#C41E3A]"
            />
            Auto-fit
          </label>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-[#2e2e2e] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 rounded-xl overflow-hidden border border-[#2e2e2e] bg-[#1a1a1a] relative">
        <MapContainer
          center={AMMAN_CENTER}
          zoom={11}
          className="h-full w-full"
          style={{ background: '#0e0e0e' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {autoCenter && bounds && <FitBoundsOnce bounds={bounds} />}

          {deliveries.map(d => {
            const driverPos: [number, number] | null = (d.driver_lat != null && d.driver_lng != null)
              ? [d.driver_lat, d.driver_lng] : null
            const destPos: [number, number] | null = (d.delivery_lat != null && d.delivery_lng != null)
              ? [d.delivery_lat, d.delivery_lng] : null

            return (
              <div key={d.order_id}>
                {driverPos && (
                  <Marker position={driverPos} icon={driverIcon}>
                    <Popup>
                      <DeliveryPopup delivery={d} />
                    </Popup>
                  </Marker>
                )}
                {destPos && (
                  <Marker position={destPos} icon={destIcon}>
                    <Popup>
                      <DeliveryPopup delivery={d} />
                    </Popup>
                  </Marker>
                )}
                {/* Connector line: ambassador → destination */}
                {driverPos && destPos && (
                  <Polyline
                    positions={[driverPos, destPos]}
                    pathOptions={{ color: '#C41E3A', weight: 2, opacity: 0.4, dashArray: '6 6' }}
                  />
                )}
              </div>
            )
          })}
        </MapContainer>

        {/* Empty state overlay — only once the first response is in, so it
            doesn't flash during the initial load. */}
        {data && trackedCount === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-[#1a1a1a]/95 border border-[#2e2e2e] rounded-xl px-6 py-4 text-center">
              <p className="text-sm text-gray-400">No active deliveries</p>
              <p className="text-xs text-gray-600 mt-1">
                Ambassadors appear here once they're out for delivery.
              </p>
            </div>
          </div>
        )}
      </div>

      <p className="text-[10px] text-gray-700 mt-2 text-right">
        Last updated {dataUpdatedAt ? formatDateTime(new Date(dataUpdatedAt).toISOString()) : '—'}
      </p>
    </div>
  )
}

/// Fits map to the given bounds the FIRST time bounds are non-empty. After that
/// the user can pan/zoom freely without being snapped back. Toggling auto-fit
/// off re-mounts this component → re-fits on next bounds change.
function FitBoundsOnce({ bounds }: { bounds: L.LatLngBounds }) {
  const map = useMap()
  useMemo(() => {
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
  }, [map, bounds])
  return null
}

function DeliveryPopup({ delivery: d }: { delivery: Delivery }) {
  const isCod = d.payment_method === 'cod' || d.payment_method === 'card_on_delivery'
  return (
    <div className="text-sm" style={{ minWidth: 220 }}>
      <p className="font-bold text-base mb-1">#{d.order_number}</p>

      <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
        <div className="flex items-center gap-1.5">
          <Truck size={12} className="text-gray-500" />
          <span className="text-xs text-gray-600">Ambassador</span>
        </div>
        <p className="text-sm font-medium">{d.driver_name}</p>
        <a href={`tel:${d.driver_phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          <Phone size={10} /> {d.driver_phone}
        </a>
        {d.driver_location_at && (
          <p className="text-[10px] text-gray-400">
            Location: {formatDateTime(d.driver_location_at)}
          </p>
        )}
      </div>

      <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
        <div className="flex items-center gap-1.5">
          <MapPin size={12} className="text-gray-500" />
          <span className="text-xs text-gray-600">Customer</span>
        </div>
        <p className="text-sm">{d.delivery_name ?? '—'}</p>
        {d.delivery_phone && (
          <a href={`tel:${d.delivery_phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <Phone size={10} /> {d.delivery_phone}
          </a>
        )}
        {d.delivery_address && (
          <p className="text-xs text-gray-600 leading-snug">{d.delivery_address}</p>
        )}
      </div>

      <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between text-xs">
        <span className="text-gray-600">{formatCurrency(d.total_fils)} · {d.payment_method.toUpperCase()}</span>
        {isCod && d.tip_fils > 0 && (
          <span className="text-[#C41E3A] font-semibold">+ {formatCurrency(d.tip_fils)} tip</span>
        )}
      </div>
    </div>
  )
}
